import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, qs } from '../api/client'
import type { Domain, LookupItem, Paged, SellItem, SellResult, Status } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { useOperation } from '../hooks/useOperation'
import { useAuth } from '../auth'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Badge, CopyListDialog, Empty, Field, Modal, Notice, Pager, fmtStamp, statusTone } from '../components/ui'
import { chamberResultNote } from '../lib/chamber'
import { copyText } from '../lib/clipboard'
import { firstSheetFitError, parkBatchOnFirstSheet } from '../lib/parkDraft'

const STYLE_OPTS = ['', 'NUMBER', 'ALPHA', 'MIX', 'REVIEW']
// 表格里显示中文短标签（和看板一致），筛选下拉仍用后端枚举值。
const STYLE_LABEL: Record<string, string> = { NUMBER: '数字', ALPHA: '字母', MIX: '混合', REVIEW: '其他' }
const STATUS_OPTS: Status[] = ['IN_STOCK', 'SOLD', 'WITHDRAWN']
// 200 是后端 /api/domains 的单页上限，选项超出它只会被服务端悄悄夹掉。
const SIZE_OPTS = [20, 50, 100, 200]

// 列顺序按操作员实际读的顺序排：先认域名，再看它是什么（区/类型/后缀/长度），
// 再看归属（平台/注册日），最后才是状态与内部字段（版本/注册码/更新）。
const COLUMNS = [
  '域名',
  '区',
  '类型',
  '后缀',
  '长度',
  '平台',
  '注册日',
  '状态',
  '版本',
  '注册码',
  '更新',
] as const

export function Inventory() {
  const [params, setParams] = useSearchParams()
  const { user } = useAuth()
  const page = Number(params.get('page') ?? '1')
  const rawSize = Number(params.get('size') ?? '50')
  const size = SIZE_OPTS.includes(rawSize) ? rawSize : 50
  const filter = {
    zone: params.get('zone') ?? '',
    status: params.get('status') ?? '',
    suffix: params.get('suffix') ?? '',
    platform: params.get('platform') ?? '',
    style: params.get('style') ?? '',
    charCount: params.get('charCount') ?? '',
    registeredOn: params.get('registeredOn') ?? '',
    q: params.get('q') ?? '',
    sort: params.get('sort') ?? 'id',
    dir: params.get('dir') ?? 'desc',
  }
  const [selected, setSelected] = useState<Map<number, SellItem>>(new Map())
  const [editing, setEditing] = useState<Domain | null>(null)
  const [confirm, setConfirm] = useState<null | 'sell' | 'restore'>(null)
  // 恢复走单个动作：已售的行不给勾选格，所以从行尾的「恢复」按钮进。
  const [restoreRow, setRestoreRow] = useState<Domain | null>(null)
  // 出库/恢复成功后弹出的清单窗：这次动了哪些域名，一眼看清并能直接复制走。
  const [done, setDone] = useState<null | { kind: 'sell' | 'restore'; domains: string[]; batchCode: string; parked?: boolean; parkError?: string }>(null)
  const [arming, setArming] = useState(false)
  const [armError, setArmError] = useState<unknown>(null)
  const [copied, setCopied] = useState<null | 'ok' | 'fail'>(null)
  const op = useOperation<SellResult>('sell')

  const { data, error, loading, reload } = useAsync(
    () => api.get<Paged<Domain>>(`/api/domains${qs({ ...filter, page, size })}`),
    [params.toString()],
  )
  const items = data?.items
  // 「在库在前、已售沉底」由后端排（和搜索页同一条 ORDER BY）：前端只重排当前
  // 这一页的话，翻页时两个板块会各自给出不同的一页，总数也就对不上了。
  const rows = useMemo(() => items ?? [], [items])
  // 只有还能出库的行给勾选：卖了的就是卖了，勾上只会让「出库」按钮灰着，像坏了。
  const selectable = useMemo(() => rows.filter((r) => r.status === 'IN_STOCK'), [rows])

  const plat = useAsync(() => api.get<{ items: LookupItem[] }>('/api/platforms'), [])
  const platforms = plat.data?.items ?? []

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params)
    if (v) next.set(k, v)
    else next.delete(k)
    if (k !== 'page') next.set('page', '1')
    setParams(next, { replace: true })
  }

  const toggle = (d: Domain) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(d.id)) next.delete(d.id)
      else next.set(d.id, { id: d.id, domain: d.domain, version: d.version })
      return next
    })
  }

  const sellable = useMemo(() => [...selected.values()], [selected])
  // 勾选是跨页的。只拿「当前页里被勾中的行」判断能不能出库，翻到没有交集的那一页
  // 按钮就灰掉，跨页挑好的货反而卖不出去。看得见的行如果已经不是在库，从选择里拿掉，
  // 版本号也跟当前行对齐，避免编辑之后整批因为一条过期版本被拒。
  useEffect(() => {
    setSelected((prev) => {
      let changed = false
      const next = new Map(prev)
      for (const r of rows) {
        const held = next.get(r.id)
        if (!held) continue
        if (r.status !== 'IN_STOCK') {
          next.delete(r.id)
          changed = true
        } else if (held.version !== r.version) {
          next.set(r.id, { id: r.id, domain: r.domain, version: r.version })
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [rows])
  // 恢复是单个动作（已售的行没有勾选格），从行尾的「恢复」进。
  const pending = useMemo<SellItem[]>(
    () => (confirm === 'restore' && restoreRow ? [{ id: restoreRow.id, domain: restoreRow.domain, version: restoreRow.version }] : sellable),
    [confirm, restoreRow, sellable],
  )

  // 一键全选只作用于当前页、且只作用在还能出库的行上：跨页"全选几千条"不是这里该给的能力，
  // 真要批量处理请收紧筛选条件；已选的项本来就跨页保留。
  const pageAllSelected = selectable.length > 0 && selectable.every((r) => selected.has(r.id))
  function togglePage() {
    setSelected((prev) => {
      const next = new Map(prev)
      if (pageAllSelected) selectable.forEach((r) => next.delete(r.id))
      else selectable.forEach((r) => next.set(r.id, { id: r.id, domain: r.domain, version: r.version }))
      return next
    })
  }

  async function chamber() {
    const items = sellable
    const domains = items.map((i) => i.domain)
    setArmError(null)
    setArming(true)
    try {
      const fit = await firstSheetFitError(domains)
      if (fit) {
        setArmError(fit)
        return
      }
      const res = await op.run((key) => api.post<SellResult>('/api/domains/sell', { items, remark: '' }, key))
      if (!res) return
      let parked = true
      let parkError = ''
      try {
        await parkBatchOnFirstSheet(domains)
      } catch (e) {
        parked = false
        parkError = e instanceof Error ? e.message : '草稿没有写上'
      }
      setConfirm(null)
      setDone({ kind: 'sell', domains, batchCode: res.batchCode, parked, parkError })
      setSelected(new Map())
      reload()
    } finally {
      setArming(false)
    }
  }

  async function copySelected() {
    setCopied((await copyText(sellable.map((s) => s.domain).join('\n'))) ? 'ok' : 'fail')
  }

  async function runTransition(kind: 'sell' | 'restore') {
    const path = kind === 'sell' ? '/api/domains/sell' : '/api/domains/restore'
    const list = kind === 'sell' ? sellable : pending
    const res = await op.run((key) => api.post<SellResult>(path, { items: list, remark: '' }, key))
    if (res) {
      setConfirm(null)
      setRestoreRow(null)
      // 状态机是整批原子提交的，所以成功时这批 items 就是被改动的全部域名。
      setDone({ kind, domains: list.map((i) => i.domain), batchCode: res.batchCode, parked: false })
      setSelected(new Map())
      reload()
    }
  }

  return (
    <section>
      {/* 副标题删掉，标题上移：这一页天天看，说明文字白占一行高度，
          把表格能显示的行数挤掉了。操作说明挪到按钮的 title 上。 */}
      <header className="page-head compact">
        <h1>域&Domain总库</h1>
      </header>

      <div className="card filters">
        <Field label="关键字">
          <input value={filter.q} onChange={(e) => setParam('q', e.target.value)} placeholder="域名片段" />
        </Field>
        <Field label="区域">
          <select value={filter.zone} onChange={(e) => setParam('zone', e.target.value)}>
            <option value="">全部</option>
            <option value="ZONE_A">A 区</option>
            <option value="ZONE_S">S 区</option>
          </select>
        </Field>
        <Field label="状态">
          <select value={filter.status} onChange={(e) => setParam('status', e.target.value)}>
            <option value="">全部</option>
            {STATUS_OPTS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <Field label="后缀">
          <input value={filter.suffix} onChange={(e) => setParam('suffix', e.target.value)} placeholder=".com" />
        </Field>
        <Field label="平台">
          <select value={filter.platform} onChange={(e) => setParam('platform', e.target.value)}>
            <option value="">全部</option>
            {filter.platform && !platforms.some((p) => p.name === filter.platform) && (
              <option value={filter.platform}>{filter.platform}（不在名单）</option>
            )}
            {platforms.map((p) => (
              <option key={p.id} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="类型">
          <select value={filter.style} onChange={(e) => setParam('style', e.target.value)}>
            {STYLE_OPTS.map((s) => (
              <option key={s} value={s}>
                {s || '全部'}
              </option>
            ))}
          </select>
        </Field>
        <Field label="字符数">
          <input
            type="number"
            min={1}
            value={filter.charCount}
            onChange={(e) => setParam('charCount', e.target.value)}
          />
        </Field>
        <Field label="注册日期">
          <input type="date" value={filter.registeredOn} onChange={(e) => setParam('registeredOn', e.target.value)} />
        </Field>
      </div>

      {/* 操作条从表格里提到外面：没有结果时也要能刷新/导出，
          而且它常驻一行，比藏在表格顶部更顺手。 */}
      <div className="row spread toolbar">
        <div className="row wrap">
          <span className="muted" role="status">
            已选 {selected.size} 项（跨页保留）
          </span>
          {selected.size > 0 && (
            <button type="button" className="link" onClick={() => void copySelected()}>
              {copied === 'ok' ? '已复制 ✓' : copied === 'fail' ? '复制失败，请手动选' : '复制所选域名'}
            </button>
          )}
        </div>
        <div className="row wrap">
          <a
            className="btn"
            href={`/api/export/domains${qs({
              zone: filter.zone,
              status: filter.status,
              suffix: filter.suffix,
              platform: filter.platform,
              style: filter.style,
              charCount: filter.charCount,
              registeredOn: filter.registeredOn,
              q: filter.q,
            })}`}
            download
            title="按当前筛选条件导出全部结果，不受当前页限制"
          >
            导出 CSV
          </a>
          <button type="button" onClick={reload} disabled={loading}>
            {loading ? '载入中…' : '刷新'}
          </button>
          <button type="button" onClick={() => setSelected(new Map())} disabled={!selected.size}>
            清除选择
          </button>
          <button
            type="button"
            className="danger"
            disabled={!user?.canWrite || sellable.length === 0}
            onClick={() => {
              op.newIntent()
              setConfirm('sell')
            }}
          >
            出库（标记为已售）
          </button>
        </div>
      </div>

      <Notice error={error} />
      {!loading && rows.length === 0 && !error && <Empty text="没有符合条件的域名。" />}

      {rows.length > 0 && (
        <div className="card table-card">
          <div className="scroll-x">
            <table className="table">
              <thead>
                <tr>
                  <th aria-label="全选本页可出库项">
                    <input
                      type="checkbox"
                      checked={pageAllSelected}
                      onChange={togglePage}
                      disabled={selectable.length === 0}
                      title={`全选/取消本页可出库的 ${selectable.length} 条（已售的不可选）`}
                      aria-label="全选本页可出库项"
                    />
                  </th>
                  {COLUMNS.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const canSell = d.status === 'IN_STOCK'
                  return (
                  <tr key={d.id} className={canSell ? (selected.has(d.id) ? 'picked' : '') : 'dim sold-row'}>
                    <td>
                      {canSell ? (
                        <input
                          type="checkbox"
                          checked={selected.has(d.id)}
                          onChange={() => toggle(d)}
                          aria-label={d.domain}
                        />
                      ) : (
                        <span className="sold-mark" title="已出库，不可勾选">·</span>
                      )}
                    </td>
                    <td className="mono">
                      <Link to={`/search?q=${encodeURIComponent(d.domain)}`}>{d.domain}</Link>
                    </td>
                    <td>{d.zone === 'ZONE_A' ? 'A' : 'S'}</td>
                    <td title={d.style}>{STYLE_LABEL[d.style] ?? d.style}</td>
                    <td>{d.suffix}</td>
                    <td>{d.charCount}</td>
                    <td>{d.platform || '-'}</td>
                    <td>{d.registeredOn || '-'}</td>
                    <td>
                      <Badge tone={statusTone(d.status)}>{d.status}</Badge>
                      {d.everSold && d.status !== 'SOLD' && <span className="tag">曾售出</span>}
                    </td>
                    <td title="乐观锁版本">v{d.version}</td>
                    <td>{d.registerCode || '-'}</td>
                    <td className="muted">{fmtStamp(d.updatedAt)}</td>
                    <td className="row-actions">
                      <button type="button" className="link" onClick={() => setEditing(d)}>
                        编辑
                      </button>
                      {d.status === 'SOLD' && (
                        <button
                          type="button"
                          className="link"
                          disabled={!user?.canWrite}
                          title="把这一条恢复为在库（卖出履历保留不删除）"
                          onClick={() => {
                            op.newIntent()
                            setRestoreRow(d)
                            setConfirm('restore')
                          }}
                        >
                          恢复
                        </button>
                      )}
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <Pager
            page={page}
            size={size}
            total={data?.total ?? 0}
            onPage={(p) => setParam('page', String(p))}
            onSize={(n) => setParam('size', String(n))}
            sizeOptions={SIZE_OPTS}
          />
        </div>
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm === 'sell' ? '确认出库' : '确认恢复为在库'}
          description={
            confirm === 'sell'
              ? `将把选中的 ${pending.length} 个域名标记为已售，并写入卖出履历。撤回请使用批次页的「整批撤回」。上膛会同时把这批追加到草稿第 1 页。`
              : `将把 ${pending[0]?.domain ?? ''} 恢复为在库；卖出履历保留不删除。`
          }
          busy={op.busy || arming}
          error={armError ?? op.error}
          copyLabel={confirm === 'sell' ? (copied === 'ok' ? '已复制 ✓' : copied === 'fail' ? '复制失败' : '复制') : undefined}
          onCopy={confirm === 'sell' ? () => void copySelected() : undefined}
          extraLabel={confirm === 'sell' ? (arming ? '上膛中…' : '上膛') : undefined}
          extraTitle="标记为已售，并追加到草稿第 1 页。下一批会先空一行再续。"
          onExtra={confirm === 'sell' ? () => void chamber() : undefined}
          onConfirm={() => void runTransition(confirm)}
          onClose={() => {
            if (op.busy || arming) return
            setArmError(null)
            setConfirm(null)
            setRestoreRow(null)
            op.newIntent()
          }}
        >
          <ul className="chips">
            {pending.slice(0, 30).map((s) => (
              <li key={s.id}>{s.domain}</li>
            ))}
            {pending.length > 30 && <li className="muted">…等 {pending.length} 个</li>}
          </ul>
        </ConfirmDialog>
      )}

      {done && (
        <CopyListDialog
          title={done.kind === 'sell' ? '本次已出库域名' : '本次已恢复为在库域名'}
          items={done.domains}
          note={
            done.parked
              ? chamberResultNote(done.batchCode, true)
              : done.parkError
                ? chamberResultNote(done.batchCode, false, done.parkError)
                : `批次 ${done.batchCode} · 一行一个，可直接粘贴到入库表或发给买家；要整批撤回就去批次页。`
          }
          onClose={() => setDone(null)}
        />
      )}

      {editing && (
        <EditDomain
          domain={editing}
          platforms={platforms}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      )}
    </section>
  )
}

function EditDomain({
  domain,
  platforms,
  onClose,
  onSaved,
}: {
  domain: Domain
  platforms: LookupItem[]
  onClose: () => void
  onSaved: () => void
}) {
  const { user } = useAuth()
  const [form, setForm] = useState({
    registerCode: domain.registerCode,
    platform: domain.platform,
    remark: domain.remark,
  })
  const op = useOperation<Domain>('edit')
  // 行上原有的名单外取值（历史遗留）保留为一个选项，否则打开这个弹窗就已经把平台
  // 改掉了：下拉里没有当前值时，浏览器的选中项就是第一项。
  const offList = form.platform && !platforms.some((p) => p.name === form.platform)

  async function save() {
    const res = await op.run((key) =>
      api.post<Domain>(
        '/api/domains',
        {
          id: domain.id,
          registerCode: form.registerCode,
          platform: form.platform,
          remark: form.remark,
        },
        key,
      ),
    )
    if (res) onSaved()
  }

  return (
    <Modal title={`编辑 ${domain.domain}`} onClose={onClose} width={520}>
      <p className="muted">
        状态 <Badge tone={statusTone(domain.status)}>{domain.status}</Badge> · 入库批次{' '}
        <Link to={`/batches?highlight=${domain.intakeBatchId}`}>#{domain.intakeBatchId}</Link> · 修改会推进版本号，
        从而让该域名所在批次的整批撤回被拒绝（防止误撤回）。
      </p>
      <div className="grid two">
        <Field label="注册码" hint="最多 64 字">
          <input
            maxLength={64}
            value={form.registerCode}
            onChange={(e) => setForm({ ...form, registerCode: e.target.value })}
          />
        </Field>
        <Field label="平台">
          <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
            {offList && <option value={form.platform}>{form.platform}（历史值）</option>}
            {!form.platform && <option value="">（未填写，清空后无法再保存为空）</option>}
            {platforms.map((p) => (
              <option key={p.id} value={p.name}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="收录日期" hint="入库时生成，不能改">
          <input type="text" value={domain.registeredOn || '—'} readOnly />
        </Field>
        <Field label="备注" hint="最多 300 字">
          <input maxLength={300} value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} />
        </Field>
      </div>
      <Notice error={op.error} />
      <div className="row end">
        <button type="button" onClick={onClose}>
          取消
        </button>
        <button type="button" className="primary" disabled={!user?.canWrite || op.busy} onClick={() => void save()}>
          {op.busy ? '保存中…' : '保存'}
        </button>
      </div>
    </Modal>
  )
}
